import type {CurrentProject} from 'common/report/model-resources/projects';
import type {TranslationKey} from 'common/i18n';
import {FeatureGate} from 'common/report/constants';
import {
  isDataStandardsEnabled,
  isIssuesHubEnabled,
  isNullPropertyMonitoringEnabled,
} from 'common/report/util/feature-sampling';

/**
 * Lexicon's sub-page ids; values double as the URL segment that selects the
 * page. This lives here rather than in `types.ts` so surfaces outside the
 * lexicon app (e.g. the command palette) can consume the nav registry without
 * pulling the app's model types into their bundle.
 */
export enum View {
  // All view (for components that always show no matter which tab)
  All = `all`,
  // Data management views
  Behaviors = `behaviors`,
  Cohorts = `cohorts`,
  CustomEvents = `custom-events`,
  CustomEventProperties = `custom-event-properties`,
  EventProperties = `event-properties`,
  Events = `events`,
  Formulas = `formulas`,
  LookupTables = `lookup-tables`,
  Metrics = `metrics`,
  ProfileProperties = `profile-properties`,
  CustomProfileProperties = `custom-profile-properties`,

  // Settings view
  EventApproval = `event-approval`,
  DataDeletion = `advanced-deletion`,
  DataRules = `data-rules`,
  VolumeMonitoring = `volume-monitoring`,
  Issues = `issues`,
}

/**
 * Path under the lexicon app root for each view. Formulas is a legacy URL
 * alias for metrics-era pages and is not a nav destination of its own.
 */
export const LEXICON_VIEW_PATHS: {[key in Exclude<View, View.All>]: string} = {
  [View.Events]: `transformations/events`,
  [View.EventProperties]: `transformations/event-properties`,
  [View.ProfileProperties]: `transformations/profile-properties`,
  [View.CustomEvents]: `transformations/custom-events`,
  [View.CustomEventProperties]: `transformations/custom-event-properties`,
  [View.LookupTables]: `transformations/lookup-tables`,
  [View.Formulas]: `transformations/formulas`,
  [View.Behaviors]: `transformations/behaviors`,
  [View.Metrics]: `transformations/metrics`,
  [View.CustomProfileProperties]: `transformations/custom-profile-properties`,

  [View.Issues]: `data-governance/issues`,
  [View.DataRules]: `data-governance/data-rules`,
  [View.DataDeletion]: `data-governance/advanced-deletion`,
  [View.EventApproval]: `data-governance/event-approval`,
  [View.VolumeMonitoring]: `data-governance/volume-monitoring`,

  [View.Cohorts]: `cohorts`,
};

/** The lexicon sidenav's section groupings, in display order. */
export enum LexiconNavSection {
  TrackedData = `tracked-data`,
  SavedDefinitions = `saved-definitions`,
  DataGovernance = `data-governance`,
}

/**
 * A navigable lexicon sub-page. The registry below is the shared source of
 * truth for which lexicon pages exist and which feature gates hide them, so
 * surfaces beyond the lexicon sidenav itself (e.g. the command palette) stay
 * in sync with what the sidenav shows.
 */
export interface LexiconNavEntry {
  view: Exclude<View, View.All>;
  /** Translation key for the page's user-visible label. */
  labelKey: TranslationKey;
  /** Path under the lexicon app root, e.g. `transformations/events`. */
  path: string;
  section: LexiconNavSection;
}

/**
 * Lexicon's navigable sub-pages in sidenav order, filtered down to what the
 * given project's feature gates allow. The Tracked Data and Saved Definitions
 * sections are ungated, so callers that only need those may omit the project.
 */
export function getLexiconNavEntries({currentProject}: {currentProject?: CurrentProject | null}): LexiconNavEntry[] {
  const entry = (
    view: Exclude<View, View.All>,
    labelKey: TranslationKey,
    section: LexiconNavSection,
  ): LexiconNavEntry => ({
    view,
    labelKey,
    path: LEXICON_VIEW_PATHS[view],
    section,
  });

  const entries: LexiconNavEntry[] = [
    entry(View.Events, `lexicon.tabs.events`, LexiconNavSection.TrackedData),
    entry(View.EventProperties, `lexicon.tabs.event-properties`, LexiconNavSection.TrackedData),
    entry(View.ProfileProperties, `lexicon.tabs.profile-properties`, LexiconNavSection.TrackedData),
    entry(View.Cohorts, `mixology.app.cohorts`, LexiconNavSection.SavedDefinitions),
    entry(View.CustomEvents, `lexicon.tabs.custom-events`, LexiconNavSection.SavedDefinitions),
    entry(View.CustomEventProperties, `lexicon.tabs.custom-event-properties`, LexiconNavSection.SavedDefinitions),
    entry(View.CustomProfileProperties, `lexicon.tabs.custom-profile-properties`, LexiconNavSection.SavedDefinitions),
    entry(View.LookupTables, `lexicon.tabs.lookup-tables`, LexiconNavSection.SavedDefinitions),
    entry(View.Metrics, `lexicon.tabs.metrics`, LexiconNavSection.SavedDefinitions),
    entry(View.Behaviors, `lexicon.tabs.behaviors`, LexiconNavSection.SavedDefinitions),
  ];

  const project = currentProject ?? undefined;
  if (isIssuesHubEnabled(project)) {
    entries.push(entry(View.Issues, `lexicon.tabs.issues`, LexiconNavSection.DataGovernance));
  }
  if (isDataStandardsEnabled(project)) {
    entries.push(entry(View.DataRules, `lexicon.tabs.data-rules`, LexiconNavSection.DataGovernance));
  }
  if (project?.getFeatureGate(FeatureGate.DataGovernance)) {
    entries.push(
      entry(View.EventApproval, `lexicon.tabs.event-approval`, LexiconNavSection.DataGovernance),
      entry(
        View.VolumeMonitoring,
        isNullPropertyMonitoringEnabled(project)
          ? `lexicon.tabs.volume-monitoring`
          : `lexicon.tabs.data-volume-monitoring`,
        LexiconNavSection.DataGovernance,
      ),
    );
  }
  entries.push(entry(View.DataDeletion, `lexicon.tabs.data-drop-and-deletion`, LexiconNavSection.DataGovernance));

  return entries;
}
